import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import ZeitraumWahl, { aktuellesQuartal, heuteIso, zeitraumText, fmtDatum, fmtZahl, fmtEuro, ladeDatei } from '../components/ZeitraumWahl';
import { hinweis } from '../components/Hinweise';

const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const BECKEN = { sportbecken: 'Sportbecken', lsb: 'Lehrschwimmbecken (LSB)' };
const AUSFALL_TYP = { ferien: 'Ferien', feiertag: 'Feiertag', schliessung: 'Hallenschließung', sonstiges: 'Sonstiges' };

const fehlerText = (err, standard) => err.response?.data?.error || standard;
const aktuellerPreis = (tarif, heute) => tarif.preise.find(p => p.gueltig_ab <= heute) || null;

// ─── Auswertung ──────────────────────────────────────────────────────────────

function SummenTabelle({ titel, zeilen, mitRechnung, rechnungen, zeitraum, onRechnung, gesperrt }) {
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>{titel}</div>
      <div className="table-wrap">
        <table>
          <thead><tr>
            <th>Bezeichnung</th><th>Termine</th><th>Std. Soll</th><th>Kosten Soll</th><th>Std. Ist</th><th>Kosten Ist</th>
            {mitRechnung && <><th>Rechnung Schwimmbad</th><th>Differenz</th></>}
          </tr></thead>
          <tbody>
            {zeilen.length === 0 && <tr><td colSpan={mitRechnung ? 8 : 6} style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Keine Daten</td></tr>}
            {zeilen.map(z => {
              const r = mitRechnung ? rechnungen.find(x => x.anbieter === z.schluessel) : null;
              return (
                <tr key={z.schluessel}>
                  <td style={{ fontWeight: 600 }}>{z.name}</td>
                  <td>{z.termine}</td>
                  <td>{fmtZahl(z.stunden_soll)}</td>
                  <td style={{ fontWeight: 700 }}>{fmtEuro(z.kosten_soll)}</td>
                  <td>{fmtZahl(z.stunden_ist)}</td>
                  <td>{fmtEuro(z.kosten_ist)}</td>
                  {mitRechnung && (
                    <>
                      <td>
                        {zeitraum.quartal ? (
                          <RechnungFeld wert={r?.betrag} onSpeichern={(betrag) => onRechnung(z.schluessel, betrag)} />
                        ) : <span className="td-muted">nur je Quartal</span>}
                      </td>
                      <td style={{ fontWeight: 700, color: r ? (Math.abs(r.differenz) < 0.005 ? '#2e7d32' : '#c62828') : undefined }}>
                        {r ? `${r.differenz > 0 ? '+' : ''}${fmtEuro(r.differenz)}` : '–'}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {mitRechnung && zeitraum.quartal && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
          Differenz = Rechnung − Soll. Positiv: das Schwimmbad berechnet mehr, als laut Belegungsplan gebucht war.
          {gesperrt ? ' Rechnungsbeträge können auch nach dem Abschluss noch eingetragen werden.' : ''}
        </div>
      )}
    </div>
  );
}

function RechnungFeld({ wert, onSpeichern }) {
  const [text, setText] = useState(wert !== undefined && wert !== null ? String(wert).replace('.', ',') : '');
  useEffect(() => { setText(wert !== undefined && wert !== null ? String(wert).replace('.', ',') : ''); }, [wert]);
  const geaendert = text !== (wert !== undefined && wert !== null ? String(wert).replace('.', ',') : '');
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <input className="form-control" style={{ width: 110, padding: '3px 6px' }} inputMode="decimal" placeholder="Betrag €"
        value={text} onChange={e => setText(e.target.value)} />
      {geaendert && <button className="btn btn-primary btn-sm" style={{ padding: '2px 8px' }} onClick={() => onSpeichern(text.trim())}>✓</button>}
    </div>
  );
}

function Auswertung() {
  const { user } = useAuth();
  const [zeitraum, setZeitraum] = useState(aktuellesQuartal);
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [termineOffen, setTermineOffen] = useState(false);
  const [entfallenOffen, setEntfallenOffen] = useState(false);

  const params = zeitraum.quartal ? { jahr: zeitraum.jahr, quartal: zeitraum.quartal } : { von: zeitraum.von, bis: zeitraum.bis };

  const load = useCallback(async () => {
    if (!zeitraum.quartal && (!zeitraum.von || !zeitraum.bis || zeitraum.von > zeitraum.bis)) return;
    setLaden(true); setFehler('');
    try { setDaten((await api.get('/api/admin/bahnen/auswertung', { params })).data); }
    catch (err) { setFehler(fehlerText(err, 'Auswertung konnte nicht geladen werden')); }
    finally { setLaden(false); }
  }, [JSON.stringify(zeitraum)]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const abgeschlossen = !!daten?.abschluss;
  const rechnung = async (anbieter, betrag) => {
    try { await api.put('/api/admin/bahnen/rechnungen', { ...zeitraum, anbieter, betrag }); load(); }
    catch (err) { hinweis.fehler(fehlerText(err, 'Speichern fehlgeschlagen')); }
  };
  const abschliessen = async () => {
    if (!window.confirm(`${zeitraumText(zeitraum)} abschließen?\n\nStunden und Kosten werden festgeschrieben. Spätere Änderungen an Preisen, Belegungsplan oder Ausfallzeiten verändern dieses Quartal nicht mehr.`)) return;
    try { await api.post('/api/admin/bahnen/abschluss', zeitraum); load(); }
    catch (err) { hinweis.fehler(fehlerText(err, 'Abschluss fehlgeschlagen')); }
  };
  const wiederOeffnen = async () => {
    if (!window.confirm(`${zeitraumText(zeitraum)} wieder öffnen?\n\nDie festgeschriebenen Zahlen werden verworfen und mit den aktuellen Daten neu berechnet.`)) return;
    try { await api.delete(`/api/admin/bahnen/abschluss/${zeitraum.jahr}/${zeitraum.quartal}`); load(); }
    catch (err) { hinweis.fehler(fehlerText(err, 'Fehlgeschlagen')); }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem', padding: '0.75rem 1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <ZeitraumWahl wert={zeitraum} onChange={setZeitraum} />
        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} disabled={!daten}
          onClick={() => ladeDatei('/api/admin/bahnen/export', params, `bahnenstunden_${zeitraum.quartal ? `${zeitraum.jahr}_Q${zeitraum.quartal}` : `${zeitraum.von}_bis_${zeitraum.bis}`}.xlsx`)}>
          📊 Excel
        </button>
      </div>

      {fehler && <div className="alert alert-error">{fehler}</div>}
      {laden && <div style={{ padding: '2rem', textAlign: 'center' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>}

      {daten && !laden && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', fontSize: '0.88rem' }}>
            {abgeschlossen ? (
              <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: 12, padding: '3px 10px', fontWeight: 700 }}>
                🔒 Abgeschlossen am {new Date(daten.abschluss.abgeschlossen_am).toLocaleDateString('de-DE')}{daten.abschluss.von ? ` von ${daten.abschluss.von}` : ''}
              </span>
            ) : <span style={{ background: '#fff8e1', color: '#e65100', borderRadius: 12, padding: '3px 10px', fontWeight: 700 }}>vorläufig</span>}
            {zeitraum.quartal && !abgeschlossen && daten.zeitraum.bis < heuteIso() && (
              <button className="btn btn-primary btn-sm" onClick={abschliessen}>🔒 Quartal abschließen</button>
            )}
            {abgeschlossen && user?.rolle === 'admin' && <button className="btn btn-secondary btn-sm" onClick={wiederOeffnen}>Wieder öffnen</button>}
          </div>

          {daten.warnungen.map(w => <div key={w} className="alert alert-error">⚠ {w}</div>)}

          <div className="stat-grid">
            <div className="stat-card"><div className="stat-label">Kosten Soll</div><div className="stat-value">{fmtEuro(daten.gesamt.kosten_soll)}</div>
              <div className="stat-sub">{fmtZahl(daten.gesamt.stunden_soll)} Bahnstunden · {daten.gesamt.termine} Termine</div></div>
            <div className="stat-card" style={{ borderLeftColor: '#2e7d32' }}><div className="stat-label">Kosten Ist (mit Training)</div><div className="stat-value">{fmtEuro(daten.gesamt.kosten_ist)}</div>
              <div className="stat-sub">{fmtZahl(daten.gesamt.stunden_ist)} Bahnstunden</div></div>
            <div className="stat-card" style={{ borderLeftColor: '#78909c' }}><div className="stat-label">Entfallen (Ausfallzeiten)</div><div className="stat-value">{daten.gesamt.entfallen}</div>
              <div className="stat-sub">Termine</div></div>
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            <strong>Soll</strong> = alle Termine laut Belegungsplan ohne Ferien, Feiertage und Schließungen.
            <strong> Ist</strong> = davon die Termine, an denen in SwimDesk ein Training gestartet wurde.
            Gleichzeitig genutzte Bahnen werden einmal berechnet und anteilig auf die Gruppen verteilt.
          </p>

          <SummenTabelle titel="Je Anbieter" zeilen={daten.je_anbieter} mitRechnung rechnungen={daten.rechnungen}
            zeitraum={zeitraum} onRechnung={rechnung} gesperrt={abgeschlossen} />
          <SummenTabelle titel="Je Tarif" zeilen={daten.je_tarif} />
          <SummenTabelle titel="Je Bahn / Becken" zeilen={daten.je_bahn} />
          <SummenTabelle titel="Je Gruppe" zeilen={daten.je_gruppe} />

          <div className="card" style={{ marginBottom: '1rem' }}>
            <button type="button" onClick={() => setTermineOffen(o => !o)} style={{ background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
              {termineOffen ? '▾' : '▸'} Einzelne Termine ({daten.termine.length})
            </button>
            {termineOffen && (
              <div className="table-wrap" style={{ marginTop: '0.5rem', maxHeight: 480, overflow: 'auto' }}>
                <table>
                  <thead><tr><th>Datum</th><th>Zeit</th><th>Gruppe</th><th>Bahn</th><th>Anteil</th><th>Preis/Std.</th><th>Std. Soll</th><th>Kosten Soll</th><th>Training</th></tr></thead>
                  <tbody>
                    {daten.termine.map((t, i) => (
                      <tr key={i} style={{ fontSize: '0.82rem' }}>
                        <td>{fmtDatum(t.datum)}</td><td>{t.von}–{t.bis}</td><td>{t.gruppe}</td>
                        <td>{t.bahn}{t.geteilt ? ' ⇄' : ''}</td><td>{t.anteil === 0.5 ? 'halb' : 'ganz'}</td>
                        <td>{fmtEuro(t.preis)}</td><td>{fmtZahl(t.stunden_soll)}</td><td>{fmtEuro(t.kosten_soll)}</td>
                        <td style={{ color: t.training ? '#2e7d32' : '#c62828' }}>{t.training ? 'ja' : 'nein'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <button type="button" onClick={() => setEntfallenOffen(o => !o)} style={{ background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
              {entfallenOffen ? '▾' : '▸'} Entfallene Termine ({daten.entfallen.length})
            </button>
            {entfallenOffen && (
              <div className="table-wrap" style={{ marginTop: '0.5rem', maxHeight: 400, overflow: 'auto' }}>
                <table>
                  <thead><tr><th>Datum</th><th>Zeit</th><th>Gruppe</th><th>Bahn</th><th>Grund</th></tr></thead>
                  <tbody>
                    {daten.entfallen.map((e, i) => (
                      <tr key={i} style={{ fontSize: '0.82rem' }}>
                        <td>{fmtDatum(e.datum)}</td><td>{e.von}–{e.bis}</td><td>{e.gruppe}</td><td>{e.bahn}</td><td>{e.grund}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Belegungsplan ───────────────────────────────────────────────────────────

function BelegungDialog({ belegung, stamm, onClose, onGespeichert }) {
  const [form, setForm] = useState(() => belegung ? { ...belegung, gueltig_bis: belegung.gueltig_bis || '', bemerkung: belegung.bemerkung || '' } : {
    gruppe_id: '', bahn_id: '', wochentag: '', von: '', bis: '', anteil: 1, gueltig_ab: heuteIso(), gueltig_bis: '', bemerkung: '',
  });
  const [fehler, setFehler] = useState('');
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFehler(''); };
  const gruppe = stamm.gruppen.find(g => g.id === Number(form.gruppe_id));

  const speichern = async () => {
    try {
      const daten = { ...form, gueltig_bis: form.gueltig_bis || null };
      if (belegung) await api.put(`/api/admin/bahnen/belegungen/${belegung.id}`, daten);
      else await api.post('/api/admin/bahnen/belegungen', daten);
      onGespeichert();
    } catch (err) { setFehler(fehlerText(err, 'Speichern fehlgeschlagen')); }
  };

  return (
    <Modal title={belegung ? 'Belegung bearbeiten' : 'Belegung hinzufügen'} onClose={onClose} size="md">
      <div className="form-group"><label className="form-label">Gruppe</label>
        <select className="form-control" value={form.gruppe_id} onChange={e => set('gruppe_id', e.target.value)} autoFocus>
          <option value="">– wählen –</option>
          {stamm.gruppen.map(g => <option key={g.id} value={g.id}>{g.name}{g.aktiv === false ? ' (inaktiv)' : ''}</option>)}
        </select></div>
      {gruppe && gruppe.termine.length > 0 && (
        <div style={{ marginTop: '-0.4rem', marginBottom: '0.75rem', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.8rem' }}>
          <span className="td-muted">Trainingszeit übernehmen:</span>
          {gruppe.termine.map((t, i) => (
            <button key={i} type="button" className="btn btn-secondary btn-sm" style={{ padding: '1px 8px' }}
              onClick={() => setForm(p => ({ ...p, wochentag: t.wochentag, von: String(t.von).slice(0, 5), bis: String(t.bis || '').slice(0, 5) }))}>
              {WOCHENTAGE[t.wochentag].slice(0, 2)} {String(t.von).slice(0, 5)}–{String(t.bis || '').slice(0, 5)}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr', gap: '0 0.6rem' }}>
        <div className="form-group"><label className="form-label">Wochentag</label>
          <select className="form-control" value={form.wochentag} onChange={e => set('wochentag', e.target.value)}>
            <option value="">–</option>{WOCHENTAGE.map((w, i) => <option key={i} value={i}>{w}</option>)}
          </select></div>
        <div className="form-group"><label className="form-label">Von</label>
          <input type="time" className="form-control" value={form.von} onChange={e => set('von', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Bis</label>
          <input type="time" className="form-control" value={form.bis} onChange={e => set('bis', e.target.value)} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0 0.6rem' }}>
        <div className="form-group"><label className="form-label">Bahn / Becken</label>
          <select className="form-control" value={form.bahn_id} onChange={e => set('bahn_id', e.target.value)}>
            <option value="">– wählen –</option>
            {stamm.bahnen.filter(b => b.aktiv || b.id === Number(form.bahn_id)).map(b => <option key={b.id} value={b.id}>{b.name} ({b.tarif_name})</option>)}
          </select></div>
        <div className="form-group"><label className="form-label">Anteil</label>
          <select className="form-control" value={form.anteil} onChange={e => set('anteil', Number(e.target.value))}>
            <option value={1}>ganz</option><option value={0.5}>halb</option>
          </select></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.6rem' }}>
        <div className="form-group"><label className="form-label">Gültig ab</label>
          <input type="date" className="form-control" value={form.gueltig_ab} onChange={e => set('gueltig_ab', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Gültig bis (leer = unbegrenzt)</label>
          <input type="date" className="form-control" value={form.gueltig_bis} min={form.gueltig_ab} onChange={e => set('gueltig_bis', e.target.value)} /></div>
      </div>
      <div className="form-group"><label className="form-label">Bemerkung</label>
        <input className="form-control" value={form.bemerkung} maxLength={300} onChange={e => set('bemerkung', e.target.value)} /></div>
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        Ändert sich die Belegung ab einem bestimmten Tag, die bisherige mit „gültig bis" beenden und eine neue anlegen –
        so bleiben frühere Zeiträume richtig berechnet.
      </p>
      {fehler && <div className="alert alert-error">{fehler}</div>}
      <div className="modal-actions">
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Abbrechen</button>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={speichern}>Speichern</button>
      </div>
    </Modal>
  );
}

function Belegungsplan({ stamm, neuLaden }) {
  const [dialog, setDialog] = useState(null);
  const [alteZeigen, setAlteZeigen] = useState(false);
  const heute = stamm.heute;
  const sichtbar = stamm.belegungen.filter(b => alteZeigen || !b.gueltig_bis || b.gueltig_bis >= heute);

  const loeschen = async (b) => {
    if (!window.confirm(`Belegung ${b.gruppe_name} · ${WOCHENTAGE[b.wochentag]} ${b.von}–${b.bis} · ${b.bahn_name} löschen?\n\nTipp: Galt sie bisher, lieber mit „gültig bis" beenden, damit vergangene Zeiträume richtig bleiben.`)) return;
    try { await api.delete(`/api/admin/bahnen/belegungen/${b.id}`); neuLaden(); }
    catch (err) { hinweis.fehler(fehlerText(err, 'Löschen fehlgeschlagen')); }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-sm" onClick={() => setDialog('neu')} disabled={!stamm.bahnen.length}>+ Belegung</button>
        {!stamm.bahnen.length && <span className="td-muted">Zuerst unter „Bahnen & Preise" Bahnen anlegen.</span>}
        <label style={{ marginLeft: 'auto', fontSize: '0.85rem', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={alteZeigen} onChange={e => setAlteZeigen(e.target.checked)} /> beendete anzeigen
        </label>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Tag</th><th>Zeit</th><th>Gruppe</th><th>Bahn / Becken</th><th>Anteil</th><th>Gültig</th><th></th></tr></thead>
          <tbody>
            {sichtbar.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1.5rem' }}>Noch keine Belegungen</td></tr>}
            {sichtbar.map(b => (
              <tr key={b.id} style={{ opacity: b.gueltig_bis && b.gueltig_bis < heute ? 0.5 : 1 }}>
                <td>{WOCHENTAGE[b.wochentag]}</td>
                <td>{b.von}–{b.bis}</td>
                <td style={{ fontWeight: 600 }}>{b.gruppe_name}</td>
                <td>{b.bahn_name}{b.bemerkung && <div className="td-muted">{b.bemerkung}</div>}</td>
                <td>{b.anteil === 0.5 ? 'halb' : 'ganz'}</td>
                <td style={{ fontSize: '0.8rem' }}>ab {fmtDatum(b.gueltig_ab)}{b.gueltig_bis ? ` bis ${fmtDatum(b.gueltig_bis)}` : ''}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-secondary btn-sm" style={{ padding: '1px 8px' }} onClick={() => setDialog(b)}>Bearbeiten</button>{' '}
                  <button className="btn btn-danger btn-sm" style={{ padding: '1px 8px' }} onClick={() => loeschen(b)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dialog && <BelegungDialog belegung={dialog === 'neu' ? null : dialog} stamm={stamm}
        onClose={() => setDialog(null)} onGespeichert={() => { setDialog(null); neuLaden(); }} />}
    </div>
  );
}

// ─── Bahnen & Preise ─────────────────────────────────────────────────────────

function BahnenPreise({ stamm, neuLaden }) {
  const [bahnDialog, setBahnDialog] = useState(null);
  const [tarifDialog, setTarifDialog] = useState(null);
  const [preisForm, setPreisForm] = useState({});
  const heute = stamm.heute;
  const minAb = stamm.abgeschlossen_bis
    ? new Date(new Date(`${stamm.abgeschlossen_bis}T12:00:00Z`).getTime() + 86400000).toISOString().slice(0, 10) : '';

  const preisAnlegen = async (tarif) => {
    const f = preisForm[tarif.id] || {};
    if (!f.preis || !f.ab) return hinweis.fehler('Bitte Preis und „gültig ab" angeben');
    const rueckwirkend = f.ab < heute;
    if (!window.confirm(`Neuer Preis für „${tarif.name}": ${f.preis} € pro Stunde ab ${fmtDatum(f.ab)}?${rueckwirkend ? '\n\nACHTUNG: Das Datum liegt in der Vergangenheit – die Kosten im noch offenen Zeitraum ab diesem Tag ändern sich.' : ''}\n\nPreise können danach nicht geändert, nur (sofern nicht abgerechnet) gelöscht werden.`)) return;
    try {
      await api.post(`/api/admin/bahnen/tarife/${tarif.id}/preise`, { preis_pro_stunde: f.preis, gueltig_ab: f.ab });
      setPreisForm(p => ({ ...p, [tarif.id]: {} }));
      neuLaden();
    } catch (err) { hinweis.fehler(fehlerText(err, 'Speichern fehlgeschlagen')); }
  };
  const preisLoeschen = async (p) => {
    if (!window.confirm(`Preis ${fmtEuro(p.preis_pro_stunde)} ab ${fmtDatum(p.gueltig_ab)} löschen?`)) return;
    try { await api.delete(`/api/admin/bahnen/preise/${p.id}`); neuLaden(); }
    catch (err) { hinweis.fehler(fehlerText(err, 'Löschen fehlgeschlagen')); }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div style={{ fontWeight: 700 }}>Tarife und Preise pro Stunde</div>
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setTarifDialog('neu')}>+ Tarif</button>
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 0 }}>
          Ein Preis gilt ab seinem Datum, bis ein neuerer Preis beginnt. Für vergangene Termine bleibt der damals gültige Preis maßgeblich.
          Halbe Belegungen (z.B. halbes LSB) werden mit dem halben Stundenpreis berechnet.
          {stamm.abgeschlossen_bis && ` Quartale bis ${fmtDatum(stamm.abgeschlossen_bis)} sind abgeschlossen – neue Preise frühestens ab ${fmtDatum(minAb)}.`}
        </p>
        {stamm.tarife.map(t => {
          const aktuell = aktuellerPreis(t, heute);
          const f = preisForm[t.id] || {};
          return (
            <div key={t.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.9rem', marginBottom: '0.6rem', opacity: t.aktiv ? 1 : 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <strong>{t.name}</strong>
                <span style={{ fontSize: '0.75rem', background: t.anbieter === 'NEW' ? '#fce4ec' : '#e3f2fd', borderRadius: 10, padding: '1px 8px' }}>{t.anbieter}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{aktuell ? `${fmtEuro(aktuell.preis_pro_stunde)} / Std.` : <span style={{ color: '#c62828' }}>kein aktueller Preis</span>}</span>
                <button className="btn btn-secondary btn-sm" style={{ padding: '1px 8px' }} onClick={() => setTarifDialog(t)}>Bearbeiten</button>
              </div>
              <div style={{ fontSize: '0.8rem', marginTop: 6 }}>
                {t.preise.length === 0 && <span className="td-muted">Noch kein Preis hinterlegt</span>}
                {t.preise.map(p => (
                  <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0', color: p === aktuell ? 'inherit' : 'var(--text-muted)' }}>
                    <span style={{ minWidth: 110 }}>ab {fmtDatum(p.gueltig_ab)}</span>
                    <span style={{ minWidth: 90, fontWeight: p === aktuell ? 700 : 400 }}>{fmtEuro(p.preis_pro_stunde)}</span>
                    {p === aktuell && <span style={{ fontSize: '0.7rem', color: '#2e7d32' }}>aktuell</span>}
                    {p.gueltig_ab > heute && <span style={{ fontSize: '0.7rem', color: '#1565c0' }}>künftig</span>}
                    {(!stamm.abgeschlossen_bis || p.gueltig_ab > stamm.abgeschlossen_bis) && (
                      <button className="btn btn-danger btn-sm" style={{ padding: '0 6px', fontSize: '0.7rem' }} onClick={() => preisLoeschen(p)}>✕</button>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.8rem' }}>Neuer Preis:</span>
                <input className="form-control" style={{ width: 100, padding: '3px 6px' }} inputMode="decimal" placeholder="€ / Std."
                  value={f.preis || ''} onChange={e => setPreisForm(p => ({ ...p, [t.id]: { ...f, preis: e.target.value } }))} />
                <span style={{ fontSize: '0.8rem' }}>ab</span>
                <input type="date" className="form-control" style={{ width: 'auto', padding: '3px 6px' }} min={minAb || undefined}
                  value={f.ab || ''} onChange={e => setPreisForm(p => ({ ...p, [t.id]: { ...f, ab: e.target.value } }))} />
                <button className="btn btn-primary btn-sm" style={{ padding: '2px 10px' }} onClick={() => preisAnlegen(t)}>Hinzufügen</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div style={{ fontWeight: 700 }}>Bahnen und Becken</div>
          <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setBahnDialog('neu')} disabled={!stamm.tarife.length}>+ Bahn / Becken</button>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Becken</th><th>Tarif</th><th>Anbieter</th><th>Belegungen</th><th></th></tr></thead>
            <tbody>
              {stamm.bahnen.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1.5rem' }}>Noch keine Bahnen angelegt</td></tr>}
              {stamm.bahnen.map(b => (
                <tr key={b.id} style={{ opacity: b.aktiv ? 1 : 0.55 }}>
                  <td style={{ fontWeight: 600 }}>{b.name}</td><td>{BECKEN[b.becken]}</td><td>{b.tarif_name}</td><td>{b.anbieter}</td><td>{b.belegungen}</td>
                  <td><button className="btn btn-secondary btn-sm" style={{ padding: '1px 8px' }} onClick={() => setBahnDialog(b)}>Bearbeiten</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {bahnDialog && <BahnDialog bahn={bahnDialog === 'neu' ? null : bahnDialog} tarife={stamm.tarife}
        onClose={() => setBahnDialog(null)} onGespeichert={() => { setBahnDialog(null); neuLaden(); }} />}
      {tarifDialog && <TarifDialog tarif={tarifDialog === 'neu' ? null : tarifDialog}
        onClose={() => setTarifDialog(null)} onGespeichert={() => { setTarifDialog(null); neuLaden(); }} />}
    </div>
  );
}

function BahnDialog({ bahn, tarife, onClose, onGespeichert }) {
  const [form, setForm] = useState(bahn || { name: '', becken: 'sportbecken', tarif_id: tarife[0]?.id || '', reihenfolge: 0, aktiv: true });
  const [fehler, setFehler] = useState('');
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFehler(''); };
  const speichern = async () => {
    try {
      if (bahn) await api.put(`/api/admin/bahnen/bahnen/${bahn.id}`, form);
      else await api.post('/api/admin/bahnen/bahnen', form);
      onGespeichert();
    } catch (err) { setFehler(fehlerText(err, 'Speichern fehlgeschlagen')); }
  };
  const loeschen = async () => {
    if (!window.confirm(`„${bahn.name}" löschen?`)) return;
    try { await api.delete(`/api/admin/bahnen/bahnen/${bahn.id}`); onGespeichert(); }
    catch (err) { setFehler(fehlerText(err, 'Löschen fehlgeschlagen')); }
  };
  return (
    <Modal title={bahn ? `Bahn: ${bahn.name}` : 'Bahn / Becken hinzufügen'} onClose={onClose} size="sm">
      <div className="form-group"><label className="form-label">Name</label>
        <input className="form-control" value={form.name} maxLength={100} placeholder="z.B. Bahn 3, LSB" autoFocus onChange={e => set('name', e.target.value)} /></div>
      <div className="form-group"><label className="form-label">Becken</label>
        <select className="form-control" value={form.becken} onChange={e => set('becken', e.target.value)}>
          {Object.entries(BECKEN).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select></div>
      <div className="form-group"><label className="form-label">Tarif (bestimmt Preis und Anbieter)</label>
        <select className="form-control" value={form.tarif_id} onChange={e => set('tarif_id', e.target.value)}>
          {tarife.map(t => <option key={t.id} value={t.id}>{t.name} ({t.anbieter})</option>)}
        </select></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.6rem', alignItems: 'end' }}>
        <div className="form-group"><label className="form-label">Sortierung</label>
          <input type="number" className="form-control" value={form.reihenfolge} onChange={e => set('reihenfolge', e.target.value)} /></div>
        <label className="form-group" style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.aktiv} onChange={e => set('aktiv', e.target.checked)} /> aktiv
        </label>
      </div>
      {bahn && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Ein Tarifwechsel wirkt auf alle noch nicht abgeschlossenen Zeiträume.</p>}
      {fehler && <div className="alert alert-error">{fehler}</div>}
      <div className="modal-actions">
        {bahn && Number(bahn.belegungen) === 0 && <button className="btn btn-danger btn-sm" onClick={loeschen}>Löschen</button>}
        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={onClose}>Abbrechen</button>
        <button className="btn btn-primary btn-sm" onClick={speichern}>Speichern</button>
      </div>
    </Modal>
  );
}

function TarifDialog({ tarif, onClose, onGespeichert }) {
  const [form, setForm] = useState(tarif || { name: '', anbieter: 'NEW', aktiv: true });
  const [fehler, setFehler] = useState('');
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFehler(''); };
  const speichern = async () => {
    try {
      if (tarif) await api.put(`/api/admin/bahnen/tarife/${tarif.id}`, form);
      else await api.post('/api/admin/bahnen/tarife', form);
      onGespeichert();
    } catch (err) { setFehler(fehlerText(err, 'Speichern fehlgeschlagen')); }
  };
  return (
    <Modal title={tarif ? `Tarif: ${tarif.name}` : 'Tarif hinzufügen'} onClose={onClose} size="sm">
      <div className="form-group"><label className="form-label">Name</label>
        <input className="form-control" value={form.name} maxLength={100} autoFocus onChange={e => set('name', e.target.value)} /></div>
      <div className="form-group"><label className="form-label">Angemietet über</label>
        <select className="form-control" value={form.anbieter} onChange={e => set('anbieter', e.target.value)}>
          <option value="NEW">NEW (Betreiber)</option><option value="Stadt">Stadt (subventioniert)</option>
        </select></div>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: '0.75rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={form.aktiv} onChange={e => set('aktiv', e.target.checked)} /> aktiv
      </label>
      {fehler && <div className="alert alert-error">{fehler}</div>}
      <div className="modal-actions">
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Abbrechen</button>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={speichern}>Speichern</button>
      </div>
    </Modal>
  );
}

// ─── Ausfallzeiten ───────────────────────────────────────────────────────────

function Ausfallzeiten({ stamm, neuLaden }) {
  const jetzt = Number(stamm.heute.slice(0, 4));
  const [jahr, setJahr] = useState(jetzt);
  const [importiert, setImportiert] = useState('');
  const [arbeitet, setArbeitet] = useState(false);
  const [dialog, setDialog] = useState(null);
  const liste = stamm.ausfallzeiten.filter(a => a.von.slice(0, 4) === String(jahr) || a.bis.slice(0, 4) === String(jahr))
    .sort((a, b) => a.von.localeCompare(b.von));

  const importieren = async () => {
    setArbeitet(true); setImportiert('');
    try {
      const r = await api.post('/api/admin/bahnen/ausfallzeiten/import', { jahr });
      setImportiert(`${r.data.neu} neue Einträge übernommen (${r.data.ferien} Ferienzeiträume, ${r.data.feiertage} Feiertage für NRW ${jahr}).`);
      neuLaden();
    } catch (err) { setImportiert(fehlerText(err, 'Import fehlgeschlagen')); }
    finally { setArbeitet(false); }
  };
  const umschalten = async (a) => {
    try { await api.put(`/api/admin/bahnen/ausfallzeiten/${a.id}`, { ...a, gilt_als_ausfall: !a.gilt_als_ausfall }); neuLaden(); }
    catch (err) { hinweis.fehler(fehlerText(err, 'Speichern fehlgeschlagen')); }
  };
  const loeschen = async (a) => {
    if (!window.confirm(`„${a.bezeichnung}" löschen?`)) return;
    try { await api.delete(`/api/admin/bahnen/ausfallzeiten/${a.id}`); neuLaden(); }
    catch (err) { hinweis.fehler(fehlerText(err, 'Löschen fehlgeschlagen')); }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <select className="form-control" style={{ width: 'auto' }} value={jahr} onChange={e => setJahr(Number(e.target.value))}>
          {[jetzt - 1, jetzt, jetzt + 1].map(j => <option key={j} value={j}>{j}</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" onClick={importieren} disabled={arbeitet}>
          {arbeitet ? 'Lade…' : `NRW-Ferien und Feiertage ${jahr} übernehmen`}
        </button>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setDialog('neu')}>+ Ausfallzeit</button>
      </div>
      {importiert && <div className="alert alert-success" style={{ fontSize: '0.85rem' }}>{importiert}</div>}
      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
        Termine an diesen Tagen zählen nicht zum Soll. Dürft ihr in bestimmten Ferien doch schwimmen, das Häkchen „Ausfall" dort entfernen.
        Wiederholtes Übernehmen legt nur fehlende Einträge an und ändert eure Einstellungen nicht.
      </p>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Ausfall</th><th>Zeitraum</th><th>Bezeichnung</th><th>Typ</th><th></th></tr></thead>
          <tbody>
            {liste.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1.5rem' }}>Keine Einträge für {jahr}</td></tr>}
            {liste.map(a => (
              <tr key={a.id} style={{ opacity: a.gilt_als_ausfall ? 1 : 0.55 }}>
                <td><input type="checkbox" checked={a.gilt_als_ausfall} onChange={() => umschalten(a)} title="Zählt als Ausfall" /></td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDatum(a.von)}{a.bis !== a.von ? ` – ${fmtDatum(a.bis)}` : ''}</td>
                <td>{a.bezeichnung}{!a.gilt_als_ausfall && <span className="td-muted"> · es wird geschwommen</span>}</td>
                <td>{AUSFALL_TYP[a.typ]}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-secondary btn-sm" style={{ padding: '1px 8px' }} onClick={() => setDialog(a)}>Bearbeiten</button>{' '}
                  <button className="btn btn-danger btn-sm" style={{ padding: '1px 8px' }} onClick={() => loeschen(a)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dialog && <AusfallDialog eintrag={dialog === 'neu' ? null : dialog}
        onClose={() => setDialog(null)} onGespeichert={() => { setDialog(null); neuLaden(); }} />}
    </div>
  );
}

function AusfallDialog({ eintrag, onClose, onGespeichert }) {
  const [form, setForm] = useState(eintrag || { von: heuteIso(), bis: heuteIso(), bezeichnung: '', typ: 'schliessung', gilt_als_ausfall: true });
  const [fehler, setFehler] = useState('');
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFehler(''); };
  const speichern = async () => {
    try {
      if (eintrag) await api.put(`/api/admin/bahnen/ausfallzeiten/${eintrag.id}`, form);
      else await api.post('/api/admin/bahnen/ausfallzeiten', form);
      onGespeichert();
    } catch (err) { setFehler(fehlerText(err, 'Speichern fehlgeschlagen')); }
  };
  return (
    <Modal title={eintrag ? 'Ausfallzeit bearbeiten' : 'Ausfallzeit hinzufügen'} onClose={onClose} size="sm">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.6rem' }}>
        <div className="form-group"><label className="form-label">Von</label>
          <input type="date" className="form-control" value={form.von} onChange={e => set('von', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Bis</label>
          <input type="date" className="form-control" value={form.bis} min={form.von} onChange={e => set('bis', e.target.value)} /></div>
      </div>
      <div className="form-group"><label className="form-label">Bezeichnung</label>
        <input className="form-control" value={form.bezeichnung} maxLength={200} placeholder="z.B. Revision Hallenbad" autoFocus onChange={e => set('bezeichnung', e.target.value)} /></div>
      <div className="form-group"><label className="form-label">Typ</label>
        <select className="form-control" value={form.typ} onChange={e => set('typ', e.target.value)}>
          {Object.entries(AUSFALL_TYP).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select></div>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: '0.75rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={form.gilt_als_ausfall} onChange={e => set('gilt_als_ausfall', e.target.checked)} /> zählt als Ausfall
      </label>
      {fehler && <div className="alert alert-error">{fehler}</div>}
      <div className="modal-actions">
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Abbrechen</button>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={speichern}>Speichern</button>
      </div>
    </Modal>
  );
}

// ─── Seite ───────────────────────────────────────────────────────────────────

const REITER = [['auswertung', '📊 Auswertung'], ['belegung', '🗓 Belegungsplan'], ['preise', '🏊 Bahnen & Preise'], ['ausfall', '🏖 Ausfallzeiten']];

export default function Bahnen() {
  const [reiter, setReiter] = useState('auswertung');
  const [stamm, setStamm] = useState(null);
  const [fehler, setFehler] = useState('');
  const [version, setVersion] = useState(0);

  const neuLaden = useCallback(() => {
    api.get('/api/admin/bahnen/stammdaten')
      .then(r => { setStamm(r.data); setVersion(v => v + 1); })
      .catch(() => setFehler('Stammdaten konnten nicht geladen werden'));
  }, []);
  useEffect(() => { neuLaden(); }, [neuLaden]);

  return (
    <div>
      <div className="page-header"><h2 className="page-title">Bahnenstunden</h2></div>
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {REITER.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setReiter(k)} className={`btn btn-sm ${reiter === k ? 'btn-primary' : 'btn-secondary'}`}>{l}</button>
        ))}
      </div>
      {fehler && <div className="alert alert-error">{fehler}</div>}
      {reiter === 'auswertung' && <Auswertung key={version} />}
      {stamm && reiter === 'belegung' && <Belegungsplan stamm={stamm} neuLaden={neuLaden} />}
      {stamm && reiter === 'preise' && <BahnenPreise stamm={stamm} neuLaden={neuLaden} />}
      {stamm && reiter === 'ausfall' && <Ausfallzeiten stamm={stamm} neuLaden={neuLaden} />}
    </div>
  );
}
